import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { X, Package, Edit, AlertTriangle, ArrowDownToLine, SlidersHorizontal, ArrowLeftRight } from 'lucide-react';
import { inventoryAPI } from '@shared/apiClient';
import InventoryStockActionModal from './InventoryStockActionModal';

const TXN_TYPE_LABELS = {
  receive: 'Received',
  adjust: 'Adjusted',
  use: 'Used on job',
  transfer_in: 'Transfer in',
  transfer_out: 'Transfer out',
  initial: 'Initial stock'
};

export default function InventoryDetailsModal({ item, isOpen, onClose, onEdit }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [stockAction, setStockAction] = useState(null);

  const { data: stockData, isLoading: stockLoading } = useQuery(
    ['inventoryStock', item?.id],
    () => inventoryAPI.getStock(item.id),
    { enabled: isOpen && !!item?.id }
  );

  const { data: txnData, isLoading: txnLoading } = useQuery(
    ['inventoryTransactions', item?.id],
    () => inventoryAPI.getTransactions(item.id, { limit: 50 }),
    { enabled: isOpen && !!item?.id && activeTab === 'history' }
  );

  if (!isOpen || !item) return null;

  const isLowStock = item.reorderPoint !== null &&
    parseFloat(item.quantityOnHand) <= parseFloat(item.reorderPoint);

  const stockRows = stockData?.data?.stock || [];
  const transactions = txnData?.data?.transactions || [];

  const handleStockSuccess = () => {
    queryClient.invalidateQueries('inventory');
    queryClient.invalidateQueries(['inventory', item.id]);
    queryClient.invalidateQueries(['inventoryStock', item.id]);
    queryClient.invalidateQueries(['inventoryTransactions', item.id]);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            onClick={onClose}
          />

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Package className="h-6 w-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{item.itemNumber}</h3>
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {isLowStock && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Low Stock
                    </span>
                  )}
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('overview')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    activeTab === 'overview' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('history')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    activeTab === 'history' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  History
                </button>
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
              {activeTab === 'overview' ? (
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setStockAction('receive')} className="btn btn-secondary btn-sm">
                      <ArrowDownToLine className="h-4 w-4 mr-1" />
                      Receive
                    </button>
                    <button type="button" onClick={() => setStockAction('adjust')} className="btn btn-secondary btn-sm">
                      <SlidersHorizontal className="h-4 w-4 mr-1" />
                      Adjust
                    </button>
                    <button type="button" onClick={() => setStockAction('transfer')} className="btn btn-secondary btn-sm">
                      <ArrowLeftRight className="h-4 w-4 mr-1" />
                      Transfer
                    </button>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Stock by Location</h4>
                    {stockLoading ? (
                      <p className="text-sm text-gray-500">Loading stock...</p>
                    ) : stockRows.length === 0 ? (
                      <p className="text-sm text-gray-500">No stock recorded at any location yet.</p>
                    ) : (
                      <div className="overflow-hidden border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {stockRows.map((row) => (
                              <tr key={row.id}>
                                <td className="px-4 py-2 text-sm text-gray-900">{row.location?.name || '—'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600 capitalize">{row.location?.type || '—'}</td>
                                <td className={`px-4 py-2 text-sm text-right font-medium ${
                                  parseFloat(row.quantityOnHand) < 0 ? 'text-red-600' : 'text-gray-900'
                                }`}>
                                  {parseFloat(row.quantityOnHand).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-900">Total</td>
                              <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">
                                {parseFloat(item.quantityOnHand).toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Pricing</h4>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Reorder Point</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {item.reorderPoint ? parseFloat(item.reorderPoint).toFixed(2) : 'Not set'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Cost</dt>
                        <dd className="mt-1 text-sm text-gray-900">${parseFloat(item.cost).toFixed(2)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Price</dt>
                        <dd className="mt-1 text-sm text-gray-900">${parseFloat(item.price).toFixed(2)}</dd>
                      </div>
                      {item.preferredVendor && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">Preferred Vendor</dt>
                          <dd className="mt-1 text-sm text-gray-900">{item.preferredVendor}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {item.manufacturerPartNumber && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">MPN</h4>
                      <p className="text-sm text-gray-700">{item.manufacturerPartNumber}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Transaction History</h4>
                  {txnLoading ? (
                    <p className="text-sm text-gray-500">Loading history...</p>
                  ) : transactions.length === 0 ? (
                    <p className="text-sm text-gray-500">No transactions recorded yet.</p>
                  ) : (
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Change</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">After</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {transactions.map((txn) => (
                            <tr key={txn.id}>
                              <td className="px-4 py-2 text-xs text-gray-600">
                                {new Date(txn.createdAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {TXN_TYPE_LABELS[txn.type] || txn.type}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {txn.location?.name || '—'}
                                {txn.relatedLocation && (
                                  <span className="block text-xs text-gray-500">
                                    ↔ {txn.relatedLocation.name}
                                  </span>
                                )}
                              </td>
                              <td className={`px-4 py-2 text-sm text-right font-medium ${
                                parseFloat(txn.quantityDelta) < 0 ? 'text-red-600' : 'text-green-700'
                              }`}>
                                {parseFloat(txn.quantityDelta) > 0 ? '+' : ''}{parseFloat(txn.quantityDelta).toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                {parseFloat(txn.quantityAfter).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                onClick={() => onEdit(item)}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Item
              </button>
              <button
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <InventoryStockActionModal
        item={item}
        mode={stockAction}
        isOpen={!!stockAction}
        onClose={() => setStockAction(null)}
        onSuccess={handleStockSuccess}
      />
    </>
  );
}
